import React, { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AdminPage } from 'components';
import { H3 } from 'design-system-web';
import { convertToUUID } from 'uuid-encoding';
import { createVisitorCustomFieldConfig } from 'graphql-queries';
import type {
  CreateVisitorCustomFieldConfigMutation,
  CreateVisitorCustomFieldConfigMutationVariables,
} from 'graphql-query-contracts';
import type { Channel } from 'lane-shared/types/ChannelType';
import type { UserType } from 'lane-shared/types/User';
import LeaveConfirmationPrompt from '../../components/LeaveConfirmationPrompt/LeaveConfirmationPrompt';
import { CustomFieldModal } from '../visitor-configuration/tabs/customFields/CustomFieldModal';
import {
  buildCreateFieldConfig,
  runCreateCustomFieldMutation,
  CUSTOM_FIELD_CREATED_SUCCESS_KEY,
  NO_ORGANIZATION_KEY,
} from '../visitor-configuration/tabs/customFields/customFieldsTabModelUtils';
import type { CustomFieldFormData } from '../../feature/components/CustomFieldsSection/customField.types';
import { useNavigateBackToCustomFields } from '../useNavigateBackToCustomFields';

export function CustomFieldCreatePage({
  channel,
}: {
  channel?: Channel;
  user?: UserType;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const navigateBack = useNavigateBackToCustomFields();
  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);
  const organizationId = channel?._id ? convertToUUID(channel._id) : null;

  const [createField] = useMutation<
    CreateVisitorCustomFieldConfigMutation,
    CreateVisitorCustomFieldConfigMutationVariables
  >(createVisitorCustomFieldConfig, { errorPolicy: 'all' });

  const handleSave = useCallback(
    async (formData: CustomFieldFormData): Promise<boolean> => {
      if (!organizationId) {
        window.Toast.show(t(NO_ORGANIZATION_KEY));

        return false;
      }

      return runCreateCustomFieldMutation({
        createField,
        config: buildCreateFieldConfig(formData, organizationId),
        t,
        showToast: (msg: string) => window.Toast.show(msg),
        onSuccess: () => {
          flushSync(() => setHasUnsavedDraft(false));
          window.Toast.show(t(CUSTOM_FIELD_CREATED_SUCCESS_KEY));
        },
      });
    },
    [createField, organizationId, t]
  );

  return (
    <>
      <LeaveConfirmationPrompt
        when={hasUnsavedDraft}
        navigate={history.push}
        title={t('web.admin.visitorManagement.configuration.leavePrompt.title')}
        description={t('web.admin.visitorManagement.configuration.leavePrompt.description')}
      />
      <AdminPage>
        <H3 as="h1">{t('web.admin.visitorManagement.customField.create.heading')}</H3>
        <CustomFieldModal
          onClose={navigateBack}
          onSave={handleSave}
          onUnsavedDraftChange={setHasUnsavedDraft}
        />
      </AdminPage>
    </>
  );
}
